/**
 * Socket Context
 *
 * Provides Socket.IO connection management for real-time updates
 * from the RMS backend (which receives data from LTS).
 *
 * Usage:
 * const { socket, isConnected, ltsConnected } = useSocket();
 *
 * Events received:
 * - lts:connected - LTS WebSocket connected to backend
 * - lts:disconnected - LTS WebSocket disconnected
 * - race:status - Race status update (flag, laps, etc.)
 * - race:positions - Competitor position updates
 * - race:passing - Individual passing event
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [ltsConnected, setLtsConnected] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);

  // Get the API URL from environment or default
  const socketUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    // Connect to the /timing namespace
    const newSocket = io(`${socketUrl}/timing`, {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      transports: ['polling', 'websocket']
    });

    // Connection events
    newSocket.on('connect', () => {
      console.log('[Socket.IO] Connected to RMS backend');
      setIsConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[Socket.IO] Disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('[Socket.IO] Connection error:', error.message);
      setIsConnected(false);
    });

    // LTS status events
    newSocket.on('lts:connected', () => {
      console.log('[Socket.IO] LTS WebSocket connected');
      setLtsConnected(true);
    });

    newSocket.on('lts:disconnected', () => {
      console.log('[Socket.IO] LTS WebSocket disconnected');
      setLtsConnected(false);
    });

    // Room join/leave confirmations
    newSocket.on('joined:race', (data) => {
      console.log('[Socket.IO] Joined race room:', data);
      setCurrentRoom(data.room);
    });

    newSocket.on('left:race', (data) => {
      console.log('[Socket.IO] Left race room:', data);
      setCurrentRoom(null);
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
    };
  }, [socketUrl]);

  /**
   * Join a race room to receive updates for a specific race
   * @param {string|number} tpeId - track_promotion_event_ID
   */
  const joinRace = useCallback((tpeId) => {
    if (socket && isConnected) {
      console.log(`[Socket.IO] Joining race: ${tpeId}`);
      socket.emit('join:race', tpeId);
    } else {
      console.warn('[Socket.IO] Cannot join race - not connected');
    }
  }, [socket, isConnected]);

  /**
   * Leave a race room to stop receiving updates
   * @param {string|number} tpeId - track_promotion_event_ID
   */
  const leaveRace = useCallback((tpeId) => {
    if (socket && isConnected) {
      console.log(`[Socket.IO] Leaving race: ${tpeId}`);
      socket.emit('leave:race', tpeId);
    }
  }, [socket, isConnected]);

  /**
   * Request current data for a race
   * @param {string|number} tpeId - track_promotion_event_ID
   */
  const requestData = useCallback((tpeId) => {
    if (socket && isConnected) {
      socket.emit('request:data', tpeId);
    }
  }, [socket, isConnected]);

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Event handler
   */
  const subscribe = useCallback((event, callback) => {
    if (socket) {
      socket.on(event, callback);
    }
  }, [socket]);

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {Function} callback - Event handler (optional, removes all if not provided)
   */
  const unsubscribe = useCallback((event, callback) => {
    if (socket) {
      if (callback) {
        socket.off(event, callback);
      } else {
        socket.off(event);
      }
    }
  }, [socket]);

  const value = {
    socket,
    isConnected,
    ltsConnected,
    currentRoom,
    joinRace,
    leaveRace,
    requestData,
    subscribe,
    unsubscribe
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

export default SocketContext;
